import { Col, Container, HeadingText, Row } from '@paypalcorp/pp-react'
import React, { Suspense } from 'react'
import _get from 'lodash/get'
import { useAppContext } from '../../contexts/app-context'
import { fptiTypes, componentNames } from '../../utils/analytics/fpti-constants'
import { Message } from '@paypalcorp/worldready-react'
import useTracking from '../../hooks/useTracking'
import ErrorBoundary from '../../shared/ErrorBoundary'
import ShimmerLoader from '../../components/loaders/shimmerLoader'

// UI Components
import Breadcrumbs from '../../components/breadcrumbs'
import ArticleLinks from '../../components/article-links'
import TopicTree from '../../components/topic-tree'
import MoreWaysOfHelp from '../../components/more-ways-of-help'
import ArticleContentError from '../../components/error-views/article-content-error'
import HeroModule from '../../components/hero-module'
import ArticleFeedback from '../../components/article-feedback'
import TabSwitcher from '../../components/tab-switcher'
import { ArticleContentTabs } from '../../components/article-content-tabs'
import { useMessage } from '../../hooks/useMessage'

import '../../styles/pages/article.less'

const Alerts = React.lazy(() =>
  import('../../components/alerts' /* webpackPrefetch: true */),
)

const Survey = React.lazy(() =>
  import('../../components/survey' /* webpackPrefetch: true */),
)

/* 
  Article page - This is the page/container for article view which will have the
  layout and components placement
*/
function Article() {
  const { trackActivity, fptiTypeByStatusCode } = useTracking()
  const { getMessage } = useMessage()
  const appCtx = useAppContext()

  const {
    pageProps,
    reqContextData: { exp },
  } = appCtx
  const { apiData, pageName, articleId } = pageProps
  const { article, topicTree, helpChannels, communityPosts } = apiData

  const isArticleContentExist = () =>
    article &&
    article.articleContent &&
    (article.articleContent.personalContent ||
      article.articleContent.businessContent)

  const isCommunityPostsExist = () =>
    Array.isArray(communityPosts) && communityPosts.length

  const isRelatedArticlesExist = () =>
    isArticleContentExist() &&
    Array.isArray(article.articleContent.relatedArticles) &&
    article.articleContent.relatedArticles.length

  const isTabSwitcherExist = () =>
    isRelatedArticlesExist() && isCommunityPostsExist()

  // <----------------------- START OF RELATED TOPICS SECTION ----------------------->
  const renderRelatedTopics = () => {
    const renderArticleLinks = ({ articlesData, componentName }) => {
      return (
        <ArticleLinks
          articlesData={articlesData}
          componentName={componentName}
        />
      )
    }

    // Tab switcher
    const renderTabSwitcher = () => {
      if (isTabSwitcherExist()) {
        const relatedTopicsTabsInfo = [
          {
            id: 'hcRelatedArticles',
            label: getMessage('app.helpcenter.helpArticles'),
            tabProps: {
              articlesData: article.articleContent.relatedArticles,
              componentName: componentNames.RELATED_ARTICLES,
            },
          },
          {
            id: 'hcArticleCommunityPosts',
            label: getMessage('app.helpcenter.communityPosts'),
            tabProps: {
              articlesData: communityPosts,
              componentName: componentNames.COMMUNITY_POSTS,
            },
          },
        ]
        return (
          <TabSwitcher
            name="relatedTopicsTabs"
            renderTabContent={renderArticleLinks}
            tabsInfo={relatedTopicsTabsInfo}
            articleId={articleId}
          />
        )
      }
      return null
    }

    const renderRelatedArticlesOrCommunityPosts = () => {
      if (!isTabSwitcherExist()) {
        if (isRelatedArticlesExist()) {
          const props = {
            articlesData: article.articleContent.relatedArticles,
            componentName: componentNames.RELATED_ARTICLES,
          }
          return renderArticleLinks(props)
        }
        if (isCommunityPostsExist()) {
          const props = {
            articlesData: communityPosts,
            componentName: componentNames.COMMUNITY_POSTS,
          }
          return renderArticleLinks(props)
        }
      }
      return null
    }

    if (isRelatedArticlesExist() || isCommunityPostsExist()) {
      return (
        <>
          <HeadingText
            className="related-articles__heading"
            as="h2"
            size="sm"
            data-testid="hc-related-topics-title"
          >
            <Message id="app.article.relatedTopics" />
          </HeadingText>
          {renderTabSwitcher()}
          {renderRelatedArticlesOrCommunityPosts()}
        </>
      )
    }

    trackActivity({
      name: componentNames.RELATED_ARTICLES,
      type: fptiTypes.NO_DATA,
      hpid: articleId,
      article_doc_id: articleId,
    })
    trackActivity({
      name: componentNames.COMMUNITY_POSTS,
      type: fptiTypes.NO_DATA,
      hpid: articleId,
      article_doc_id: articleId,
    })
    return null
  }

  // <----------------------- END OF RELATED TOPICS SECTION ----------------------->

  // ARTICLE FEEDBACK SECTION
  const renderArticleFeedback = () => {
    if (isArticleContentExist()) {
      const helpComponentIds = _get(
        article,
        'articleContent.ecmComponentIds',
        '',
      )

      return (
        <ArticleFeedback
          articleId={articleId}
          helpComponentIds={helpComponentIds}
        />
      )
    }
    return null
  }

  // TOPIC TREE SECTION
  const renderTopicTree = () => {
    const { data, isError, statusCode } = topicTree

    if (!isError && Array.isArray(data) && data.length) {
      const breadcrumbPath = _get(
        article,
        'articleContent.breadCrumbPath',
        null,
      )

      return <TopicTree topicTreeData={data} breadCrumbPath={breadcrumbPath} />
    }

    // FPTI Tracking
    const type = isError && fptiTypeByStatusCode(statusCode)
    trackActivity({
      name: componentNames.TOPIC_TREE,
      type,
      hpid: articleId,
      article_doc_id: articleId,
    })
    return null
  }

  // ARTICLE CONTENT SECTION
  const renderArticleContent = () => {
    if (isArticleContentExist()) {
      return (
        <ArticleContentTabs
          articleContent={article.articleContent}
          articleId={articleId}
          accountType={exp}
        />
      )
    }

    const { isError, statusCode } = article
    const type = isError && fptiTypeByStatusCode(statusCode)

    trackActivity({
      name: componentNames.ARTICLE_CONTENT,
      type,
      hpid: articleId,
      article_doc_id: articleId,
    })
    return <ArticleContentError />
  }

  // BREADCRUMB SECTION
  const renderBreadCrumb = () => {
    if (
      isArticleContentExist() &&
      article.articleContent.breadCrumbPath &&
      article.articleContent.breadCrumbTitle
    ) {
      const { breadCrumbPath, breadCrumbTitle } = article.articleContent
      return (
        <Breadcrumbs
          breadCrumbPath={breadCrumbPath}
          breadCrumbTitle={breadCrumbTitle}
        />
      )
    }
    return null
  }

  // MORE WAYS OF HELP SECTION
  const renderMoreWaysOfHelp = () => {
    const { data, isError, statusCode } = helpChannels
    if (!isError && Array.isArray(data) && data.length) {
      return <MoreWaysOfHelp helpChannels={data} />
    }

    const type = isError && fptiTypeByStatusCode(statusCode)
    trackActivity({
      name: componentNames.MORE_WAYS_OF_HELP,
      type,
      hpid: articleId,
      article_doc_id: articleId,
    })

    return null
  }

  return (
    <>
      <Container>
        <Row>
          <Suspense fallback={<ShimmerLoader />}>
            <Alerts pageName={pageName} />
          </Suspense>
        </Row>
      </Container>

      <HeroModule />

      <Container>
        <Col
          className="hc-breadcrumb-container"
          md={{ display: 'block', span: 12 }}
          xs={false}
        >
          {renderBreadCrumb()}
        </Col>

        <Row className="topic-tree-article">
          <Col
            xs={false}
            md={{ display: 'block', span: 4 }}
            lg={{ display: 'block', span: 3 }}
          >
            {renderTopicTree()}
          </Col>
          <Col md={1} />
          <Col>
            {/* We are wrapping error boundaries with individual widgets/components to protect crashing the rest of the application */}
            <ErrorBoundary name={componentNames.ARTICLE_CONTENT}>
              {renderArticleContent()}
            </ErrorBoundary>
            {renderArticleFeedback()}
            {renderRelatedTopics()}
          </Col>
        </Row>

        <Row>{renderMoreWaysOfHelp()}</Row>
        <Row>
          <Suspense fallback={<ShimmerLoader />}>
            <Survey />
          </Suspense>
        </Row>
      </Container>
    </>
  )
}

export default Article
